/**
 * subjectSelfComp.js — "is this comp actually the subject property?"
 *
 * Frontend mirror of functions/utils/subjectSelfComp.js. Kept as a separate copy
 * because src/ cannot import from functions/ — if you change the matching rules
 * in one, change them in the other, or the ARV Workspace's comp pool and the
 * backend's comp pool disagree about how many comps exist.
 *
 * Why this exists: the comp pool (compFetcherV1 snapshot, or the legacy AWS
 * gateway) can contain the subject property itself — it is a real record inside
 * the search polygon, and a listed subject lands in the `forSale` bucket like
 * any other active listing. Showing (and letting the user select) the subject as
 * a comp for itself is circular.
 *
 * Conservative by design — a false positive silently deletes a legitimate comp,
 * which is worse than leaving one self-comp in. Three independent signals, any
 * one sufficient:
 *   1. zpid (`uniqueIds.zillowId`, float-shape normalised: "12345.0" → "12345")
 *   2. ReAPI id (`uniqueIds.propertyId` / `uniqueIds.reapiId`) — this is the
 *      lead's own propertyId, so it works even when the response carries no
 *      subject record, only the id we asked with.
 *   3. street line + 5-digit ZIP — the same house can carry two zpids. Street
 *      suffixes are canonicalised ("Ave" ≡ "Avenue") but the house number and
 *      any unit token are kept verbatim, so "123 Main St Apt 2" does NOT match
 *      "123 Main St" — otherwise every sibling unit in a duplex vanishes.
 */

// ⚠️ Map, not an object literal. A plain-object lookup keyed on address text
// reaches Object.prototype: a street token of "constructor" returns a FUNCTION
// (truthy) and gets stringified into the key, and "__proto__" returns an object.
// Address strings come from third-party scrapers, so the key space is not ours
// to trust. Map has no prototype chain, so only real entries can ever match.
const STREET_SUFFIX = new Map(Object.entries({
    ave: 'avenue', av: 'avenue', avenue: 'avenue',
    st: 'street', str: 'street', street: 'street',
    dr: 'drive', drive: 'drive',
    rd: 'road', road: 'road',
    ln: 'lane', lane: 'lane',
    ct: 'court', court: 'court',
    blvd: 'boulevard', boulevard: 'boulevard',
    pl: 'place', place: 'place',
    ter: 'terrace', terr: 'terrace', terrace: 'terrace',
    cir: 'circle', circle: 'circle',
    pkwy: 'parkway', parkway: 'parkway',
    hwy: 'highway', highway: 'highway',
    trl: 'trail', trail: 'trail',
    way: 'way'
}));

const normId = (v) => {
    if (v === null || v === undefined || v === '') return null;
    // An id is a string or a finite number — nothing else. Without this,
    // String(v) happily turns NaN into "NaN", true into "true" and an object
    // into "[object Object]", any of which becomes a live identity that every
    // comp carrying the same junk then matches — the same fail-CLOSED shape as
    // the zero-id case below. NaN is the realistic one: any upstream parseInt
    // over a missing field produces it. (It also means a value with a throwing
    // toString can never reach String(v) — JSON data can't produce one, but a
    // throw here would blank the whole ARV tab.)
    if (typeof v !== 'string' && !(typeof v === 'number' && Number.isFinite(v))) return null;
    const s = String(v).trim();
    if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return null;
    const id = s.split('.')[0];
    // ⚠️ A zero id is a MISSING id, never a real one — some sources encode an
    // absent zillowId/propertyId as 0 rather than null. Treating it as a value
    // is the one way this filter can fail CLOSED: subject zpid 0 would match
    // every comp that also has 0 and silently delete most of the pool. Anything
    // that is all zeros is not an identity.
    return /^0+$/.test(id) ? null : id;
};

const normalizeAddress = (address) => {
    if (!address || typeof address !== 'string') return '';
    return address
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')   // strip combining accent marks
        .replace(/[.,#]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

// Directionals, expanded like street types — "SW" ≡ "Southwest". A trailing
// directional is the norm in Atlanta and Kansas City, this codebase's two
// biggest comp markets.
// ⚠️ Map, not an object literal. A plain-object lookup keyed on address text
// reaches Object.prototype: a street token of "constructor" returns a FUNCTION
// (truthy) and gets stringified into the key, and "__proto__" returns an object.
// Address strings come from third-party scrapers, so the key space is not ours
// to trust. Map has no prototype chain, so only real entries can ever match.
const DIRECTIONAL = new Map(Object.entries({
    n: 'north', north: 'north',
    s: 'south', south: 'south',
    e: 'east', east: 'east',
    w: 'west', west: 'west',
    ne: 'northeast', northeast: 'northeast',
    nw: 'northwest', northwest: 'northwest',
    se: 'southeast', southeast: 'southeast',
    sw: 'southwest', southwest: 'southwest'
}));

// Expansion runs over EVERY token after the house number, not just the last.
// Canonicalising only the final token left "1070 camilla st sw" unable to match
// "1070 camilla street sw" — the address signal was dead on exactly the
// addresses it exists for. Symmetric and idempotent, so it only adds correct
// matches; no token is ever dropped, so distinct streets cannot collide.
const streetLineKey = (street) => {
    if (!street || typeof street !== 'string') return '';
    const s = normalizeAddress(street);
    if (!/^\d/.test(s)) return '';
    const tokens = s.split(' ').filter(Boolean);
    if (tokens.length < 2) return '';
    for (let i = 1; i < tokens.length; i++) {
        tokens[i] = STREET_SUFFIX.get(tokens[i]) || DIRECTIONAL.get(tokens[i]) || tokens[i];
    }
    return tokens.join(' ');
};

const zipKey = (postalCode) => {
    const z = normId(postalCode);
    return z ? z.replace(/[^0-9]/g, '').slice(0, 5) : '';
};

/**
 * @param {Object|null} subjectPropertyDetails - AWS/compFetcherV1 `propertyDetails`
 * @param {string|number|null} [fallbackPropertyId] - the lead's own propertyId
 */
export function buildSubjectIdentity(subjectPropertyDetails, fallbackPropertyId = null) {
    const s = subjectPropertyDetails || {};
    const ids = s.uniqueIds || {};
    const addr = s.addressDetails || {};

    const zpids = new Set();
    const zpid = normId(ids.zillowId);
    if (zpid) zpids.add(zpid);

    const propertyIds = new Set();
    [ids.propertyId, ids.reapiId, fallbackPropertyId].forEach(v => {
        const n = normId(v);
        if (n) propertyIds.add(n);
    });

    const street = streetLineKey(addr.streetAddress);
    const zip = zipKey(addr.postalCode);
    const addressKey = street && zip ? `${street}|${zip}` : null;

    return {
        zpids,
        propertyIds,
        addressKey,
        isEmpty: zpids.size === 0 && propertyIds.size === 0 && !addressKey
    };
}

/** @returns {'zpid'|'propertyId'|'address'|null} */
export function subjectMatchReason(comp, identity) {
    if (!comp || !identity || identity.isEmpty) return null;
    const c = comp.propertyDetails || comp;
    const ids = c.uniqueIds || {};

    const zpid = normId(ids.zillowId);
    if (zpid && identity.zpids.has(zpid)) return 'zpid';

    const reapi = [ids.propertyId, ids.reapiId];
    for (let i = 0; i < reapi.length; i++) {
        const n = normId(reapi[i]);
        if (n && identity.propertyIds.has(n)) return 'propertyId';
    }

    if (identity.addressKey) {
        const addr = c.addressDetails || {};
        const street = streetLineKey(addr.streetAddress);
        const zip = zipKey(addr.postalCode);
        if (street && zip && `${street}|${zip}` === identity.addressKey) return 'address';
    }

    return null;
}

export function isSubjectComp(comp, identity) {
    return subjectMatchReason(comp, identity) !== null;
}

/**
 * Drop every self-comp from a list. Returns the input array untouched when the
 * identity carries no usable signal, so a missing subject record can never
 * empty the pool.
 */
export function excludeSubjectComps(comps, identity) {
    const list = Array.isArray(comps) ? comps : [];
    if (!identity || identity.isEmpty) return { kept: list, dropped: [] };
    const kept = [], dropped = [];
    list.forEach(c => (subjectMatchReason(c, identity) ? dropped : kept).push(c));
    return { kept, dropped };
}
